// Service worker registration, kept out of main.jsx so it can be tested
// without mounting the app.
//
// Registration is production-only on purpose: in dev, Vite serves unhashed
// modules and a cache-first worker will happily serve you yesterday's code
// while you edit today's. That has cost more debugging hours across the
// industry than the feature has ever saved in dev.

/**
 * @returns {Promise<{registered: boolean, reason?: string, registration?: unknown}>}
 *   Always resolves. A failed registration must not take the page down with it
 *   — the site works perfectly well without a service worker; it just is not
 *   installable or offline-capable.
 */
export async function registerServiceWorker(options = {}) {
  const nav = options.navigator ?? globalThis.navigator
  const isProduction = options.isProduction ?? Boolean(import.meta.env?.PROD)
  const logger = options.logger ?? console
  const scriptUrl = options.scriptUrl ?? '/sw.js'

  if (!isProduction) {
    return { registered: false, reason: 'not a production build' }
  }
  if (!nav || !('serviceWorker' in nav)) {
    return { registered: false, reason: 'serviceWorker unsupported in this browser' }
  }

  try {
    const registration = await nav.serviceWorker.register(scriptUrl, { scope: '/' })
    return { registered: true, registration }
  } catch (err) {
    // Named, not swallowed. Registration fails for real, diagnosable reasons —
    // an insecure origin, a 404 on the script, a scope violation — and each one
    // is worth seeing in a console rather than inferring from "install isn't
    // offered".
    logger.error(`service worker registration failed: ${err?.message ?? err}`)
    return { registered: false, reason: String(err?.message ?? err) }
  }
}
